import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import Table from "@cloudscape-design/components/table";
import Button from "@cloudscape-design/components/button";
import Modal from "@cloudscape-design/components/modal";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Textarea from "@cloudscape-design/components/textarea";
import Box from "@cloudscape-design/components/box";
import Alert from "@cloudscape-design/components/alert";
import Select from "@cloudscape-design/components/select";

import CONFIG from "../config";

function DocumentTypeManager({ idToken, onLogout }) {
  const { t } = useTranslation();
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState({ visible: false, type: null });
  const [deleteModal, setDeleteModal] = useState({ visible: false, type: null });
  const [alert, setAlert] = useState(null);

  // Form state
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formFields, setFormFields] = useState([]);

  const handleUnauthorized = useCallback(() => {
    if (onLogout) {
      onLogout();
    }
  }, [onLogout]);

  const loadDocumentTypes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${CONFIG.apiUrl}document-types`, {
        headers: { Authorization: idToken },
      });

      if (response.status === 401 || response.status === 403) {
        handleUnauthorized();
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setDocumentTypes(data.document_types);
      }
    } catch (error) {
      console.error("Failed to load document types:", error);
      if (error.message === "Failed to fetch") {
        handleUnauthorized();
      }
    } finally {
      setLoading(false);
    }
  }, [idToken, handleUnauthorized]);

  useEffect(() => {
    loadDocumentTypes();
  }, [loadDocumentTypes]);

  const resetForm = () => {
    setFormName("");
    setFormDescription("");
    setFormFields([]);
  };

  const handleCreate = async () => {
    if (!formName) {
      setAlert({ type: "error", message: t("venue.documents.type_manager.nameRequired") });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${CONFIG.apiUrl}document-types`, {
        method: "POST",
        headers: {
          Authorization: idToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formName,
          description: formDescription,
          metadata_fields: formFields,
        }),
      });

      if (response.status === 401 || response.status === 403) {
        handleUnauthorized();
        return;
      }

      if (response.ok) {
        setAlert({ type: "success", message: t("venue.documents.type_manager.createSuccess") });
        setCreateModal(false);
        resetForm();
        loadDocumentTypes();
      } else {
        throw new Error(t("venue.documents.type_manager.createFailed"));
      }
    } catch (error) {
      setAlert({ type: "error", message: error.message });
      if (error.message === "Failed to fetch") {
        handleUnauthorized();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!formName) {
      setAlert({ type: "error", message: t("venue.documents.type_manager.nameRequired") });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${CONFIG.apiUrl}document-types/${editModal.type.id}`, {
        method: "PUT",
        headers: {
          Authorization: idToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formName,
          description: formDescription,
          metadata_fields: formFields,
        }),
      });

      if (response.status === 401 || response.status === 403) {
        handleUnauthorized();
        return;
      }

      if (response.ok) {
        setAlert({ type: "success", message: t("venue.documents.type_manager.updateSuccess") });
        setEditModal({ visible: false, type: null });
        resetForm();
        loadDocumentTypes();
      } else {
        throw new Error(t("venue.documents.type_manager.updateFailed"));
      }
    } catch (error) {
      setAlert({ type: "error", message: error.message });
      if (error.message === "Failed to fetch") {
        handleUnauthorized();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${CONFIG.apiUrl}document-types/${deleteModal.type.id}`, {
        method: "DELETE",
        headers: { Authorization: idToken },
      });

      if (response.status === 401 || response.status === 403) {
        handleUnauthorized();
        return;
      }

      if (response.ok) {
        setAlert({ type: "success", message: t("venue.documents.type_manager.deleteSuccess") });
        setDeleteModal({ visible: false, type: null });
        loadDocumentTypes();
      } else {
        throw new Error(t("venue.documents.type_manager.deleteFailed"));
      }
    } catch (error) {
      setAlert({ type: "error", message: error.message });
      if (error.message === "Failed to fetch") {
        handleUnauthorized();
      }
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (type) => {
    setFormName(type.name);
    setFormDescription(type.description || "");

    // Handle metadata_fields - could be string (from old data) or array
    let fields = type.metadata_fields || [];
    if (typeof fields === "string") {
      try {
        fields = JSON.parse(fields);
      } catch (e) {
        console.error("Failed to parse metadata_fields:", e);
        fields = [];
      }
    }
    setFormFields(fields);
    setEditModal({ visible: true, type });
  };

  const openCreateModal = () => {
    resetForm();
    setCreateModal(true);
  };

  const addField = () => {
    setFormFields([...formFields, { name: "", type: "text", required: false }]);
  };

  const updateField = (index, key, value) => {
    const newFields = [...formFields];
    newFields[index][key] = value;
    setFormFields(newFields);
  };

  const removeField = (index) => {
    setFormFields(formFields.filter((_, i) => i !== index));
  };

  const fieldTypeOptions = [
    { label: t("venue.documents.type_manager.fieldTypes.text"), value: "text" },
    { label: t("venue.documents.type_manager.fieldTypes.number"), value: "number" },
    { label: t("venue.documents.type_manager.fieldTypes.date"), value: "date" },
    { label: t("venue.documents.type_manager.fieldTypes.boolean"), value: "boolean" },
    { label: t("venue.documents.type_manager.fieldTypes.email"), value: "email" },
    { label: t("venue.documents.type_manager.fieldTypes.url"), value: "url" },
  ];

  const renderFormFields = () => (
    <SpaceBetween size="m">
      <FormField label={t("venue.documents.type_manager.name")}>
        <Input value={formName} onChange={({ detail }) => setFormName(detail.value)} placeholder={t("venue.documents.type_manager.namePlaceholder")} />
      </FormField>

      <FormField label={t("venue.documents.type_manager.description")}>
        <Textarea
          value={formDescription}
          onChange={({ detail }) => setFormDescription(detail.value)}
          placeholder={t("venue.documents.type_manager.descriptionPlaceholder")}
          rows={2}
        />
      </FormField>

      <FormField label={t("venue.documents.type_manager.metadataFields")} description={t("venue.documents.type_manager.metadataFieldsDescription")}>
        <SpaceBetween size="s">
          {formFields.map((field, index) => (
            <div key={index} style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}>
                {index === 0 && <Box variant="awsui-key-label">{t("venue.documents.type_manager.fieldName")}</Box>}
                <Input
                  value={field.name}
                  onChange={({ detail }) => updateField(index, "name", detail.value)}
                  placeholder={t("venue.documents.type_manager.fieldNamePlaceholder")}
                />
              </div>
              <div style={{ flex: 1 }}>
                {index === 0 && <Box variant="awsui-key-label">{t("venue.documents.type_manager.fieldType")}</Box>}
                <Select
                  selectedOption={fieldTypeOptions.find((opt) => opt.value === field.type)}
                  onChange={({ detail }) => updateField(index, "type", detail.selectedOption.value)}
                  options={fieldTypeOptions}
                />
              </div>
              <Button onClick={() => removeField(index)} iconName="remove" variant="icon" ariaLabel={t("venue.documents.type_manager.remove")} />
            </div>
          ))}
          <Button onClick={addField} iconName="add-plus">
            {t("venue.documents.type_manager.addField")}
          </Button>
        </SpaceBetween>
      </FormField>
    </SpaceBetween>
  );

  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            <Button variant="primary" onClick={openCreateModal}>
              {t("venue.documents.type_manager.createButton")}
            </Button>
          }
        >
          {t("venue.documents.type_manager.title")}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {alert && (
          <Alert type={alert.type} dismissible onDismiss={() => setAlert(null)}>
            {alert.message}
          </Alert>
        )}

        <Table
          columnDefinitions={[
            {
              id: "name",
              header: t("venue.documents.type_manager.name"),
              cell: (item) => item.name,
            },
            {
              id: "description",
              header: t("venue.documents.type_manager.description"),
              cell: (item) => item.description || "-",
            },
            {
              id: "fields",
              header: t("venue.documents.type_manager.metadataFields"),
              cell: (item) => {
                let fields = item.metadata_fields || [];
                if (typeof fields === "string") {
                  try {
                    fields = JSON.parse(fields);
                  } catch (e) {
                    return 0;
                  }
                }
                return Array.isArray(fields) ? fields.length : 0;
              },
            },
            {
              id: "actions",
              header: t("venue.documents.type_manager.actions"),
              cell: (item) => (
                <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={() => openEditModal(item)} iconName="edit" variant="icon" ariaLabel={t("venue.documents.type_manager.edit")} />
                  <Button
                    onClick={() => setDeleteModal({ visible: true, type: item })}
                    iconName="remove"
                    variant="icon"
                    ariaLabel={t("venue.documents.type_manager.delete")}
                  />
                </SpaceBetween>
              ),
            },
          ]}
          items={documentTypes}
          loading={loading}
          empty={
            <Box textAlign="center" color="inherit">
              <b>{t("venue.documents.type_manager.noDocumentTypes")}</b>
              <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                {t("venue.documents.type_manager.noDocumentTypesDescription")}
              </Box>
            </Box>
          }
        />

        {/* Create Modal */}
        <Modal
          visible={createModal}
          onDismiss={() => setCreateModal(false)}
          header={t("venue.documents.type_manager.createModalTitle")}
          size="large"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setCreateModal(false)}>
                  {t("venue.common.cancel")}
                </Button>
                <Button variant="primary" onClick={handleCreate} loading={loading}>
                  {t("venue.documents.type_manager.create")}
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          {renderFormFields()}
        </Modal>

        {/* Edit Modal */}
        <Modal
          visible={editModal.visible}
          onDismiss={() => setEditModal({ visible: false, type: null })}
          header={t("venue.documents.type_manager.editModalTitle")}
          size="large"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setEditModal({ visible: false, type: null })}>
                  {t("venue.common.cancel")}
                </Button>
                <Button variant="primary" onClick={handleUpdate} loading={loading}>
                  {t("venue.documents.type_manager.update")}
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          {renderFormFields()}
        </Modal>

        {/* Delete Modal */}
        <Modal
          visible={deleteModal.visible}
          onDismiss={() => setDeleteModal({ visible: false, type: null })}
          header={t("venue.documents.type_manager.deleteModalTitle")}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setDeleteModal({ visible: false, type: null })}>
                  {t("venue.common.cancel")}
                </Button>
                <Button variant="primary" onClick={handleDelete} loading={loading}>
                  {t("venue.common.delete")}
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          {t("venue.documents.type_manager.deleteModalMessage", { typeName: deleteModal.type?.name })}
        </Modal>
      </SpaceBetween>
    </Container>
  );
}

export default DocumentTypeManager;
